<template>
  <div class="news">
    <loading :loaded="isLoaded">
      <div key="content">
        <div class="h2 notice-title">{{ $t("newsPage.pageTitle") }}</div>

        <!-- カテゴリタブ -->
        <div class="category-tabs">
          <div class="tab-label">{{ $t("newsPage.categoryLabel") }}</div>
          <div class="tabs-container">
            <div
              v-for="category in categories"
              :key="category.value"
              class="tab-item"
              :class="{ active: selectedCategory === category.value }"
              @click="onCategoryChange(category.value)"
            >
              {{ category.label }}
            </div>
          </div>
        </div>

        <div v-if="news.length === 0" class="empty-message">{{ $t("newsPage.emptyMessage") }}</div>
        <div v-else class="accbox">
          <div class="news-item" v-for="newsItem in news" :key="newsItem.id">
            <input type="checkbox" :id="newsItem.id" class="cssacc" />
            <label :for="newsItem.id" class="news-header">
              <div v-if="isNewItem(newsItem.publishedAt)" class="news-badge">NEW</div>
              <div class="news-meta">
                <span class="news-date">{{ formatDate(newsItem.publishedAt) }}</span>
                <span class="news-category">
                  {{ getCategoryLabel(newsItem) }}
                </span>
              </div>
              <div class="news-title">{{ newsItem.title }}</div>
            </label>
            <div class="accshow">
              <div class="news-content" v-html="newsItem.text"></div>
            </div>
          </div>
        </div>
        <div v-if="news.length > 0" class="pager">
          <div class="pager-context">
            {{
              $t("newsPage.pagerContext", {
                start: currentPage * limit - limit + 1,
                end: Math.min(currentPage * limit, totalCount),
                total: totalCount,
              })
            }}
          </div>
          <paginate
            :key="`paginate-${selectedCategory}`"
            :value="currentPage"
            :page-count="getPageCount"
            :click-handler="clickCallback"
            :container-class="'pagination'"
            :page-class="'page-item'"
            :prev-class="'prev'"
            :next-class="'next'"
          ></paginate>
        </div>
      </div>
    </loading>
    <div class="space"></div>
    <site-footer class="footer"></site-footer>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import SiteFooter from "@/components/organisms/Footer.vue";
import Loading from "@/components/atoms/Loading.vue";
import { showNotifyDialog } from "@/lib/dialog";
import axiosBase from "axios";
import { News, NewsCategory } from "@/lib/models/general";
import { differenceInDays, format } from "date-fns";

const Paginate = require("vuejs-paginate");

const axios = axiosBase.create({
  baseURL: process.env.VUE_APP_MICROCMS_API_ENDPOINT,
  headers: { "X-API-KEY": process.env.VUE_APP_MICROCMS_API_KEY },
});

export default Vue.extend<Data, Methods, Computed, Props>({
  // NOTE: metaタグの設定
  metaInfo: {
    meta: [
      {
        name: "robots",
        content: "none",
      },
    ],
  },
  components: {
    SiteFooter,
    Loading,
    Paginate,
  },
  data() {
    return {
      news: [] as News[],
      limit: 15,
      currentPage: 1,
      totalCount: 0,
      selectedCategory: NewsCategory.all as NewsCategory,
      isLoaded: false,
    };
  },
  computed: {
    getPageCount() {
      return Math.ceil(this.totalCount / this.limit);
    },
    categories() {
      return [
        { value: NewsCategory.all, label: this.$t("newsPage.categories.all") as string },
        { value: NewsCategory.newFeature, label: this.$t("newsPage.categories.newFeature") as string },
        { value: NewsCategory.commercialWork, label: this.$t("newsPage.categories.commercialWork") as string },
        { value: NewsCategory.contestCampaign, label: this.$t("newsPage.categories.contestCampaign") as string },
        { value: NewsCategory.companyBoard, label: this.$t("newsPage.categories.companyBoard") as string },
        { value: NewsCategory.interview, label: this.$t("newsPage.categories.interview") as string },
        { value: NewsCategory.bugMaintenance, label: this.$t("newsPage.categories.bugMaintenance") as string },
        { value: NewsCategory.other, label: this.$t("newsPage.categories.other") as string },
      ];
    },
  },
  methods: {
    async getNews() {
      try {
        // localeフィールドが["ja-JP"]と未設定のもののみを取得
        let filters = "locale[not_exists][or]locale[contains]ja-JP";

        // publishedAtが存在するもののみを取得
        filters += "[and]publishedAt[exists]";

        // カテゴリが選択されている場合（all以外）、カテゴリフィルタを追加
        // NOTE: categoryIdは配列として返ってくるため、[contains]オペレーターを使用
        if (this.selectedCategory !== NewsCategory.all) {
          filters += `[and]categoryId[contains]${this.selectedCategory}`;
        }

        const url = `/news?limit=${this.limit}&offset=${
          (this.currentPage - 1) * this.limit
        }&filters=${filters}&orders=-publishedAt`;
        const newsRequest = await axios.get(url);

        if (newsRequest.status !== 200) {
          await showNotifyDialog({
            title: this.$t("newsPage.errorTitle") as string,
            content: this.$t("newsPage.errorMessage") as string,
          });
        }
        this.news = newsRequest.data.contents;
        this.totalCount = newsRequest.data.totalCount;
      } catch (error) {
        await showNotifyDialog({
          title: this.$t("newsPage.errorTitle") as string,
          content: this.$t("newsPage.errorMessage") as string,
        });
      } finally {
        this.isLoaded = true;
      }
    },
    readNews() {
      if (this.news.length === 0) return;
      const latestNews = this.news[0];
      this.$store.dispatch("generalModule/readNews", latestNews);
    },
    clickCallback(pageNum) {
      this.currentPage = Number(pageNum);
      this.getNews();
      window.scrollTo(0, 0);
    },
    onCategoryChange(category: NewsCategory) {
      this.selectedCategory = category;
      this.currentPage = 1; // ページをリセット
      this.getNews();
      window.scrollTo(0, 0);
    },
    formatDate(dateString: string) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return format(date, this.$t("newsPage.dateFormat") as string);
    },
    getCategoryLabel(newsItem: News) {
      // 1. categoryCustomTextがあれば最優先で返す
      if (newsItem.categoryCustomText) {
        return newsItem.categoryCustomText;
      }

      // 2. categoryIdがない、または空配列の場合は「該当カテゴリなし」を返す
      if (!newsItem.categoryId || newsItem.categoryId.length === 0) {
        return this.$t("newsPage.noCategory") as string;
      }

      // 3. 最初のcategoryIdに対応するカテゴリを検索
      const categoryValue = newsItem.categoryId[0];
      const category = this.categories.find((cat) => cat.value === categoryValue);

      // 4. 該当するカテゴリが見つからない場合は「該当カテゴリなし」を返す
      if (!category) {
        return this.$t("newsPage.noCategory") as string;
      }

      // 5. 該当するカテゴリのラベルを返す
      return category.label;
    },
    isNewItem(publishedAt: string) {
      if (!publishedAt) return false;
      const publishedDate = new Date(publishedAt);
      const now = new Date();
      const daysDiff = differenceInDays(now, publishedDate);
      return daysDiff < 7;
    },
  },
  async created() {
    await this.getNews();
    this.readNews();
  },
});

interface Props {}

interface Data {
  news: News[];
  limit: number;
  currentPage: number;
  totalCount: number;
  selectedCategory: NewsCategory;
  isLoaded: boolean;
}

interface Computed {
  getPageCount: number;
  categories: Array<{ value: NewsCategory; label: string }>;
}

interface Methods {
  getNews(): Promise<void>;
  readNews(): void;
  clickCallback(pageNum: any): void;
  onCategoryChange(category: NewsCategory): void;
  formatDate(dateString: string): string;
  getCategoryLabel(newsItem: News): string;
  isNewItem(publishedAt: string): boolean;
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

@mixin latestNews($width, $padding, $grid-gap) {
  .h2 {
    margin-left: $padding;
    font-size: x-large;
    @include mq(sp) {
      font-size: var(--font-size-md);
    }
  }
}

@include mq(sp) {
  @include latestNews($width: 150px, $padding: 10px, $grid-gap: 10px);
}

@include mq(pc) {
  @include latestNews($width: 250px, $padding: 100px, $grid-gap: 50px);
}

@include mq(sm) {
  // tablet
  @include latestNews($width: 180px, $padding: 50px, $grid-gap: 40px);
}

@include mq(md) {
  // tablet
  @include latestNews($width: 200px, $padding: 50px, $grid-gap: 40px);
}

.notice-title {
  border-bottom: none !important;
  color: var(--color-text-normal);
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-md);
}

/* カテゴリタブ */
.category-tabs {
  margin: 20px 100px;
  display: flex;
  align-items: center;
  gap: 20px;

  @include mq(sp) {
    margin: 15px 0;
    gap: 0;
  }

  @include mq(sm) {
    margin: 15px 50px;
  }

  @include mq(md) {
    margin: 15px 50px;
  }

  .tab-label {
    font-size: var(--font-size-sm);
    color: var(--color-text-normal);
    font-weight: var(--font-weight-regular);
    white-space: nowrap;

    @include mq(sp) {
      display: none; // スマホでは非表示
    }
  }

  .tabs-container {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;

    @include mq(sp) {
      flex-wrap: nowrap; // 折り返しなし
      overflow-x: auto; // 横スクロール有効
      -webkit-overflow-scrolling: touch; // スムーズスクロール（iOS）
      gap: 6px;
      padding: 0 10px; // 左右に余白
      width: 100%; // 幅いっぱい

      // スクロールバーを非表示（見た目をスッキリ）
      &::-webkit-scrollbar {
        display: none;
      }
      -ms-overflow-style: none;
      scrollbar-width: none;
    }
  }

  .tab-item {
    position: relative;
    padding: 4px 16px;
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-bold);
    color: var(--color-text-low);
    background-color: var(--color-surface-muted);
    border-radius: var(--radius-lg);
    cursor: pointer;
    white-space: nowrap;

    @include hover-overlay;

    @include mq(sp) {
      padding: 5px 12px;
      font-size: var(--font-size-xs);
    }

    &.active {
      color: var(--color-text-invert);
      background-color: var(--color-background-invert);
      font-weight: var(--font-weight-bold);

      &::after {
        display: none;
      }
    }
  }
}

.news {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 60px);
  background-color: var(--color-background-normal);
  color: var(--color-text-normal);
}
.space {
  flex-grow: 1;
}

.empty-message {
  margin: 50px 100px;
  padding: 40px;
  text-align: center;
  font-size: var(--font-size-lg);
  color: var(--color-text-low);
  background: var(--color-surface-muted);
  border-radius: var(--radius-sm);

  @include mq(sp) {
    margin: 20px 10px;
    padding: 30px;
    font-size: var(--font-size-md);
  }

  @include mq(sm) {
    margin: 30px 50px;
  }

  @include mq(md) {
    margin: 30px 50px;
  }
}

/*ボックス全体*/
.accbox {
  margin: 20px 100px;
  padding: 0;
  width: calc(100% - 200px);

  @include mq(sp) {
    margin: 20px 10px;
    width: calc(100% - 20px);
  }
  @include mq(sm) {
    margin: 20px 50px;
    width: calc(100% - 100px);
  }
  @include mq(md) {
    margin: 20px 50px;
    width: calc(100% - 100px);
  }
}

.news-item {
  margin-bottom: 12px;
  border-radius: var(--radius-sm);
  background: var(--color-surface-base);
  overflow: hidden;
}

/*チェックは隠す*/
.cssacc {
  display: none;
}

/*ラベル（ヘッダー部分）*/
.news-header {
  display: block;
  padding: 16px 20px;
  cursor: pointer;
  transition: background-color 0.2s;

  @include mq(sp) {
    padding: 12px 16px;
  }
}

.news-badge {
  color: var(--color-warning);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);

  @include mq(sp) {
    font-size: var(--font-size-xxs);
    margin-bottom: 2px;
  }
}

.news-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  font-size: var(--font-size-sm);

  @include mq(sp) {
    font-size: var(--font-size-xs);
    gap: 8px;
    margin-bottom: 6px;
  }
}

.news-date {
  color: var(--color-text-low);
}

.news-category {
  padding: 2px 8px;
  color: var(--color-text-low);
  font-size: var(--font-size-xs);

  @include mq(sp) {
    padding: 2px 6px;
    font-size: var(--font-size-xs);
  }
}

.news-title {
  color: var(--color-text-normal);
  font-weight: var(--font-weight-regular);
  font-size: var(--font-size-md);
  line-height: 1.5;

  @include mq(sp) {
    font-size: var(--font-size-sm);
  }
}

/*中身を非表示にしておく*/
.accshow {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.3s ease;
}

.accshow > .news-content {
  overflow: hidden;
  min-height: 0;
}

/*クリックで中身表示*/
.cssacc:checked ~ .accshow {
  grid-template-rows: 1fr;
  transition: grid-template-rows 0.5s ease;
}

.news-content {
  padding: 0 20px;
  color: var(--color-text-normal);
  font-size: var(--font-size-sm);
  line-height: 1.7;

  @include mq(sp) {
    padding: 0 16px 16px;
    font-size: var(--font-size-sm);
  }

  ::v-deep {
    * {
      color: var(--color-text-normal);
    }

    a {
      color: var(--color-primary);
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      color: var(--color-text-normal);
    }

    code {
      background-color: var(--color-surface-muted);
      color: var(--color-text-normal);
    }

    pre {
      background-color: var(--color-surface-muted);
      color: var(--color-text-normal);
    }

    blockquote {
      border-left-color: var(--color-border);
      color: var(--color-text-normal);
    }
  }
}

/* paginate */
.pager {
  width: 80%;
  margin: 20px auto;

  .pager-context {
    text-align: center;
    color: var(--color-text-normal);
  }

  .pagination {
    margin-top: 30px;
    margin-bottom: 50px;
    padding: 0;
    text-align: center;

    ::v-deep li {
      display: inline-block;
    }

    ::v-deep li > a {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      margin: 0 1em;
      border-radius: var(--radius-circle);
      color: var(--color-text-low);
      background-color: var(--color-surface-muted);
      border-color: var(--color-border);
      font-size: var(--font-size-sm);
      outline: none;
    }

    ::v-deep li.active > a {
      color: var(--color-text-invert);
      background-color: var(--color-background-invert);
      border-color: var(--color-background-invert);
      font-weight: var(--font-weight-bold);
    }

    ::v-deep .prev,
    ::v-deep .next {
      display: none;
    }
  }
}
</style>
